import { Link } from "react-router-dom";
import { ArrowRight, Check, Download } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { SectionDiagram } from "../sections/SectionDiagram";
import { DealerCTA } from "../sections/DealerCTA";
import { BRAND_IMG, DOOR_VARIANTS, WINDOW_FRAME_SPECS, CATALOGUE_PDF } from "../data/site";

export default function Products() {
  return (
    <main>
      <PageHero
        kicker="Products"
        title="Galvanized Steel Door & Window Frames"
        desc="Pressed steel frames manufactured from 1.5 mm GPSP steel with 275 GSM galvanization — built for professional construction."
      />

      <section data-testid="door-frames-product" className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div className="bg-brand-mist border border-black/10 p-4 sm:p-6 flex flex-col gap-6 items-center justify-center">
                <img
                  src="/brand/product-door-real.jpg"
                  alt="MODORS galvanized steel door frames — real product photograph"
                  className="w-full h-[340px] sm:h-[420px] object-cover object-top"
                />
                <img
                  src="/brand/profile-render.png"
                  alt="MODORS door frame profile — double rebate, 90 degree bends, 12 mm return"
                  className="w-full max-w-md drop-shadow-[0_20px_30px_rgba(0,0,0,0.18)]"
                />
              </div>
            </Reveal>
            <div>
              <SectionHeading kicker="Product 01" title="MODORS Galvanized Steel Door Frames" />
              <Reveal delay={0.1}>
                <p className="mt-5 text-sm text-brand-ink/55 leading-relaxed max-w-lg">
                  Two series for every project and budget — single and double rebate
                  options, manufactured with consistent precision.
                </p>
              </Reveal>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {DOOR_VARIANTS.map((v, vi) => (
                  <Reveal key={v.name} delay={0.08 * vi}>
                    <div data-testid={`door-variant-${vi}`} className="border border-black/10 bg-white h-full">
                      <div className="bg-brand-ink px-5 py-4 flex items-center justify-between gap-3">
                        <span className="font-display text-lg font-semibold uppercase tracking-wide text-white">
                          {v.name}
                        </span>
                        <span className="text-[11px] font-semibold tracking-[0.1em] uppercase text-brand-red whitespace-nowrap">
                          {v.section}
                        </span>
                      </div>
                      <table className="w-full" data-testid={`door-variant-spec-table-${vi}`}>
                        <tbody>
                          {v.specs.map((s, i) => (
                            <tr key={s.label} className={i % 2 === 0 ? "bg-brand-mist" : "bg-white"}>
                              <td className="px-4 py-3 text-[11px] font-semibold tracking-[0.14em] uppercase text-brand-steel border-b border-black/10">
                                {s.label}
                              </td>
                              <td className="px-4 py-3 text-right font-display text-[13px] font-semibold uppercase text-brand-ink border-b border-black/10">
                                {s.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.28}>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="door-frames-details-btn"
                    className="group inline-flex items-center gap-3 bg-brand-red text-white text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 hover:bg-brand-ink transition-colors duration-300"
                  >
                    Request Product Details
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                  <a
                    href={CATALOGUE_PDF}
                    download="MODORS-Catalogue.pdf"
                    data-testid="products-catalogue-btn"
                    className="group inline-flex items-center gap-3 border border-brand-ink/30 text-brand-ink text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 hover:border-brand-red hover:text-brand-red transition-colors duration-300"
                  >
                    Download Catalogue
                    <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="window-frames-product" className="bg-brand-mist py-20 sm:py-28 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="lg:order-2">
            <Reveal>
              <div className="bg-white border border-black/10 p-8 sm:p-14 flex items-center justify-center relative">
                <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-brand-red" />
                <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-brand-red" />
                <img
                  src={BRAND_IMG.windowFrames}
                  alt="MODORS galvanized steel window frames"
                  className="w-full max-w-lg drop-shadow-[0_20px_30px_rgba(0,0,0,0.18)]"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:order-1">
            <SectionHeading
              kicker="Product 02"
              title="MODORS Steel Window Frames"
              desc="Galvanized steel window frames, precision fabricated for consistent fit and finish across every unit."
            />
            <Reveal delay={0.12}>
              <table className="mt-9 w-full border border-black/10 bg-white" data-testid="window-frame-spec-table">
                <tbody>
                  {WINDOW_FRAME_SPECS.map((s, i) => (
                    <tr key={s.label} className={i % 2 === 0 ? "bg-brand-mist" : "bg-white"}>
                      <td className="px-5 py-4 text-xs font-semibold tracking-[0.2em] uppercase text-brand-steel border-b border-black/10">
                        {s.label}
                      </td>
                      <td className="px-5 py-4 text-right font-display text-lg font-semibold uppercase text-brand-ink border-b border-black/10">
                        {s.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                to="/contact"
                data-testid="window-frames-details-btn"
                className="group mt-9 inline-flex items-center gap-3 bg-brand-ink text-white text-sm font-semibold tracking-[0.14em] uppercase px-8 py-4 hover:bg-brand-red transition-colors duration-300"
              >
                Request Product Details
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <SectionDiagram />
      <DealerCTA />
    </main>
  );
}
